import React, {useEffect, useState} from 'react'
import styled from 'styled-components'
import SelectPopup from '../../../components/SelectPopup/SelectPopup'
import {getRecurrenceLists, getWeekDay} from '../../../utils/recurrence'
import ListSelect from '../../../components/ListSelect/ListSelect'
import CalendarField from '../../../components/Calendar/CalendarField/CalendarField'
import TimeSegment from '../../../components/TimeSegment/TimeSegment'
import Icons from '../../../components/Icons'
import WeekDaySelect from '../../../components/WeekDaySelect/WeekDaySelect'
import {fetchRoomsByDepartment, fetchRoomTypes} from '../../../api/room'
import {
  useWorkspaceDispatch,
  useWorkspaceState
} from '../../../context/WorkspaceContext'
import {
  setDrawingHighlights,
  useDrawingDispatch
} from '../../../context/DrawingContext'
import {_L, useLocaleState} from '../../../context/LocaleContext'
import {useUIState} from '../../../context/UIContext'
import Spinner from '../../../components/Spinner/Spinner'

const highlightRooms = ({drawingDispatch, highlights, show}) => {
  setDrawingHighlights({
    dispatch: drawingDispatch,
    assetType: 'rm',
    highlights,
    show: show
  })
}

const highlightRoomsForSelectedDepartment = ({
  drawingDispatch,
  deptCode,
  departments
}) => {
  if (departments.length > 0 && deptCode) {
    let highlights = {}
    const department = departments.filter(item => item.deptCode === deptCode)[0]
    const {dv_id, dp_id, rooms} = department
    let show = false

    if (dv_id && dp_id) {
      show = true
    }
    if (show) {
      rooms.forEach(room => {
        highlights[room] = {
          stroke: '#2dcab5',
          fill: '#2dcab5',
          strokeWidth: '0.3%'
        }
      })
    }
    highlightRooms({drawingDispatch, highlights, show})
  }
}

const WorkspaceFilter = ({
  onSearch,
  bookingTimes,
  showClose,
  onCloseFilter
}) => {
  const localeState = useLocaleState()
  let {search} = useWorkspaceState()
  const uiState = useUIState()

  const workspaceDispatch = useWorkspaceDispatch()
  const drawingDispatch = useDrawingDispatch()
  const recurrenceLists = getRecurrenceLists({localeState})
  const [roomTypes, setRoomTypes] = useState([])
  const [departments, setDepartments] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const {
    date_start,
    date_end,
    dayPart,
    recurrenceType,
    weekDays,
    frequencyDay,
    frequency,
    rm_type,
    deptCode
  } = search

  const {activeFloor} = uiState
  const {blId, flId} = activeFloor

  useEffect(
    () => {
      let didCancel = false
      const fetchData = async () => {
        const anyText = _L({key: 'Any', localeState})

        if (blId && flId) {
          fetchRoomTypes({blId, flId}).then(roomTypes => {
            // Add the 'Any' element to the room type list
            roomTypes.unshift({
              rmType: '',
              description: anyText
            })
            if (!didCancel) {
              setRoomTypes(roomTypes)
            }
          })
          fetchRoomsByDepartment({blId, flId}).then(departmentsByFloor => {
            let departments = Object.values(departmentsByFloor)
              .filter(item => !!item.dp_id)
              .sort((a, b) => a.department.localeCompare(b.department))
            // Add the 'Any' element to the department list
            departments.unshift({
              dv_id: '',
              dp_id: '',
              department: anyText,
              deptCode: ''
            })
            if (!didCancel) {
              setDepartments(departments)
            }
          })
        }
      }

      fetchData()

      return () => {
        didCancel = true
      }
    },
    [blId, flId, localeState]
  )

  useEffect(
    () => {
      highlightRoomsForSelectedDepartment({
        drawingDispatch,
        deptCode,
        departments
      })
    },
    [departments, deptCode, drawingDispatch]
  )

  const workspaceSetSearch = payload =>
    workspaceDispatch({type: 'setWorkspaceSearch', payload})

  const doSearch = async () => {
    setIsLoading(true)
    await onSearch(search)
    highlightRoomsForSelectedDepartment({
      drawingDispatch,
      deptCode,
      departments
    })
    setIsLoading(false)
  }

  const onDayPartSelected = dayPart =>
    workspaceSetSearch({
      dayPart: dayPart.selectedIndex
    })

  const onSelectDays = day => workspaceSetSearch({frequencyDay: day.value})

  const onSelectStartDate = date => {
    let date_start = new Date(date)
    let newState = {date_start}

    if (
      recurrenceType === 'none' ||
      date_end.getTime() < date_start.getTime()
    ) {
      newState = {date_start, date_end: date_start}
    }

    workspaceSetSearch(newState)
  }

  const onSelectEndDate = date => workspaceSetSearch({date_end: new Date(date)})

  const onSelectRecurrenceType = type => {
    let newState = {recurrenceType: type.value}
    // Set the selected week day to the current date
    if (type.value === 'week') {
      let dayIndex = getWeekDay(new Date())
      let weekDays = [0, 0, 0, 0, 0, 0, 0]
      weekDays[dayIndex] = 1
      newState = {...newState, ...{weekDays}}
    }

    workspaceSetSearch(newState)
  }

  const onSelectFrequency = frequency =>
    workspaceSetSearch({frequency: frequency.value})

  const onRoomTypeSelect = roomTypeInfo => {
    const {rmType: rm_type} = roomTypeInfo
    workspaceSetSearch({rm_type})
  }

  const onDepartmentSelect = department => {
    resetSelectedHighlights().then(() => {
      const {dv_id, dp_id, deptCode} = department
      workspaceSetSearch({dv_id, dp_id, deptCode})
    })
  }

  const resetSelectedHighlights = async () => {
    // setHighlightAfterSearch(false)
    let highlights = {}
    const {deptCode} = search
    if (deptCode) {
      const {rooms} = departments.filter(
        item => item['deptCode'] === deptCode
      )[0]
      rooms.forEach(room => {
        highlights[room] = {}
      })
    }
    await highlightRooms({drawingDispatch, highlights, show: false})
  }

  const onWeekDaySelect = weekDays =>
    workspaceSetSearch({weekDays: weekDays.slice(0)})

  const {TYPES, DAYS, FREQUENCY} = recurrenceLists
  const showEndDate = recurrenceType !== 'none'
  const showDaysOfWeek = recurrenceType === 'week'
  const showFrequency = recurrenceType === 'month'

  return (
    <Container>
      <Title>{_L({key: 'Workspace Booking', localeState})}</Title>

      {showClose && (
        <CloseIcon onClick={onCloseFilter}>
          <Icons icon="CLOSE" height={14} width={14} />
        </CloseIcon>
      )}

      <FilterContainer>
        <FilterLayout>
          <RecurrenceContainer>
            <ComponentContainer>
              <Label>{_L({key: 'Recurrence Pattern', localeState})}</Label>
              <ListSelect
                options={TYPES}
                placeholder=""
                isSearchable={false}
                value={TYPES.filter(({value}) => value === recurrenceType)}
                onChange={onSelectRecurrenceType}
              />
            </ComponentContainer>

            <ComponentContainer>
              <Label>{_L({key: 'Start Date', localeState})}</Label>
              <CalendarField
                onSelect={onSelectStartDate}
                date={date_start.toISOString()}
              />
            </ComponentContainer>

            {showEndDate && (
              <ComponentContainer>
                <Label>{_L({key: 'End Date', localeState})}</Label>
                <CalendarField
                  onSelect={onSelectEndDate}
                  date={date_end.toISOString()}
                />
              </ComponentContainer>
            )}

            {showDaysOfWeek && (
              <ComponentContainer>
                <Label>{_L({key: 'Days of Week', localeState})}</Label>
                <WeekDaySelect value={weekDays} onChange={onWeekDaySelect} />
              </ComponentContainer>
            )}

            {showFrequency && (
              <ComponentContainer style={{paddingBottom: 16}}>
                <Label>{_L({key: 'Every', localeState})}</Label>
                <ListSelect
                  options={FREQUENCY}
                  placeholder=""
                  isSearchable={false}
                  value={FREQUENCY.filter(({value}) => value === frequency)}
                  onChange={onSelectFrequency}
                />
              </ComponentContainer>
            )}
            {showFrequency && (
              <ComponentContainer>
                <ListSelect
                  options={DAYS}
                  placeholder=""
                  isSearchable={false}
                  value={DAYS.filter(({value}) => value === frequencyDay)}
                  onChange={onSelectDays}
                />
              </ComponentContainer>
            )}
          </RecurrenceContainer>

          <DayPartContainer>
            <ComponentContainer>
              <TimeSegment
                timeRanges={bookingTimes}
                onSelect={onDayPartSelected}
                value={dayPart}
              />
            </ComponentContainer>

            <ComponentContainer>
              <PopupLabel>{_L({key: 'Room Type', localeState})}</PopupLabel>
              <SelectPopup
                value={rm_type}
                options={roomTypes}
                onSelect={onRoomTypeSelect}
                valueField="rmType"
                displayField="description"
              />
            </ComponentContainer>

            <ComponentContainer>
              <PopupLabel>
                {_L({key: 'Show Department', localeState})}
              </PopupLabel>
              <SelectPopup
                value={deptCode}
                options={departments}
                onSelect={onDepartmentSelect}
                valueField="deptCode"
                displayField="department"
              />
            </ComponentContainer>
          </DayPartContainer>
        </FilterLayout>
      </FilterContainer>

      <SearchButton onClick={doSearch}>
        {isLoading ? (
          <Spinner height={26} width={26} />
        ) : (
          _L({key: 'Search', localeState})
        )}
      </SearchButton>
    </Container>
  )
}

export default WorkspaceFilter

const Container = styled.div`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
`

const FilterContainer = styled.div`
  padding: 10px 24px 24px 24px;
  height: calc(100vh - 205px);
  overflow-y: auto;
  box-sizing: border-box;
  flex: 1;
  width: 100%;

  @media (max-width: 768px) {
    height: 100%;
  }

  @media (max-width: 420px) {
    height: calc(100vh - 114px);
  }
`

const FilterLayout = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: 1fr;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-template-rows: 1fr;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 20px;
  }

  @media (max-width: 420px) {
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr;
    grid-row-gap: 20px;
  }
`

const RecurrenceContainer = styled.div`
  grid-row: 1;
  grid-column: 1;
  box-sizing: border-box;
`

const DayPartContainer = styled.div`
  grid-row: 2;
  grid-column: 1;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-row: 1;
    grid-column: 2;
  }
  @media (max-width: 420px) {
    grid-row: 2;
    grid-column: 1;
  }
`

const ComponentContainer = styled.div`
  box-sizing: border-box;
  padding-bottom: 10px;
`

const Label = styled.div`
  font-size: 16px;
  color: #a4a9b7;
  box-sizing: border-box;
  padding-top: 10px;
  padding-bottom: 10px;
`

const Title = styled.div`
  font-size: 22px;
  height: 60px;
  padding: 26px 24px 24px 24px;
  box-sizing: border-box;
  font-weight: bold;
  line-height: 26px;
`

const SearchButton = styled.button`
  height: 64px;
  width: 100%;
  background-color: #3395fe;
  color: white;
  text-align: center;
  font-size: 18px;
`

const CloseIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 38px;
  width: 38px;
  border-radius: 3px;
  background-color: #f4f5fa;
  cursor: pointer;
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 25;
`
const PopupLabel = styled(Label)`
  padding-bottom: 2px;
`
